
import { useState } from 'react';
import { AlertTriangle, CheckCircle2, RefreshCw, Loader2, Search, ChevronDown, ChevronUp, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';

function DeviationBadge({ value, label }) {
    if (value === null || value === undefined) return null;
    if (value === 0) {
        return (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
                ✓ OK
            </span>
        );
    }
    const isNegative = value < 0;
    return (
        <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${
                isNegative
                    ? 'bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30'
                    : 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30'
            }`}
            title={label}
        >
            {isNegative ? '↓' : '↑'} R$ {Math.abs(value).toFixed(2)}
        </span>
    );
}

export default function BillingAuditPanel() {
    const [auditData, setAuditData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [filter, setFilter] = useState('all'); // all, discrepancy, no_asaas
    const [searchTerm, setSearchTerm] = useState('');
    const [syncingId, setSyncingId] = useState(null);
    const [isSyncingAll, setIsSyncingAll] = useState(false);

    const runAudit = async () => {
        setIsLoading(true);
        try {
            const res = await apiFetch('/api/internal/billing/audit');
            if (res.ok) {
                const data = await res.json();
                setAuditData(data);
                setIsOpen(true);
            } else {
                toast.error('Erro ao executar auditoria');
            }
        } catch (error) {
            console.error('Audit failed:', error);
            toast.error('Erro ao executar auditoria');
        } finally {
            setIsLoading(false);
        }
    };

    const syncAccount = async (accountId, companyName) => {
        setSyncingId(accountId);
        try {
            const res = await apiFetch(`/api/internal/billing/sync/${accountId}`, { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                toast.success(`MRR de ${companyName} recalculado: R$ ${Number(data.mrrValue).toFixed(2)}`);
                // Refresh audit data
                await runAudit();
            } else {
                toast.error(`Erro ao sincronizar ${companyName}`);
            }
        } catch (error) {
            console.error('Sync failed:', error);
            toast.error(`Erro ao sincronizar ${companyName}`);
        } finally {
            setSyncingId(null);
        }
    };

    const syncAll = async () => {
        setIsSyncingAll(true);
        try {
            const res = await apiFetch('/api/internal/billing/sync-all', { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Sync concluído: ${data.synced} atualizados, ${data.errors} erros`);
                await runAudit();
            } else {
                toast.error('Erro ao sincronizar todas as contas');
            }
        } catch (error) {
            console.error('Sync all failed:', error);
            toast.error('Erro ao sincronizar todas as contas');
        } finally {
            setIsSyncingAll(false);
        }
    };

    const filteredAccounts = auditData?.accounts?.filter((a) => {
        if (filter === 'discrepancy' && !a.hasDiscrepancy) return false;
        if (filter === 'no_asaas' && a.asaasFound) return false;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (
                a.companyName?.toLowerCase().includes(term) ||
                a.cnpj?.includes(term)
            );
        }
        return true;
    }) || [];

    return (
        <div className="bg-surface/60 border border-border rounded-xl overflow-hidden">
            {/* Header */}
            <button
                type="button"
                className="flex items-center justify-between w-full p-4 cursor-pointer hover:bg-surface-hover transition-colors text-left"
                onClick={() => {
                    if (!auditData && !isLoading) {
                        runAudit();
                    } else {
                        setIsOpen(!isOpen);
                    }
                }}
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/15 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-foreground-strong">Auditoria de Billing</h3>
                        <p className="text-xs text-foreground-muted">
                            {auditData
                                ? `${auditData.summary.withDiscrepancy} discrepâncias · ${auditData.summary.withoutAsaas} sem Asaas · ${auditData.summary.total} contas`
                                : 'Clique para verificar MRR vs Asaas'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {auditData && (
                        <button
                            onClick={(e) => { e.stopPropagation(); runAudit(); }}
                            disabled={isLoading}
                            className="p-1.5 hover:bg-surface-active rounded-lg transition-colors text-foreground-muted hover:text-foreground-strong"
                            title="Recarregar auditoria"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>
                    )}
                    {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-foreground-muted" />
                    ) : isOpen ? (
                        <ChevronUp className="w-4 h-4 text-foreground-muted" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-foreground-muted" />
                    )}
                </div>
            </button>

            {/* Content */}
            {isOpen && auditData && (
                <div className="border-t border-border">
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-4">
                        <div className="bg-surface-active/50 rounded-lg p-2.5 text-center">
                            <div className="text-lg font-bold text-foreground-strong">{auditData.summary.total}</div>
                            <div className="text-[10px] text-foreground-muted uppercase tracking-wider">Total</div>
                        </div>
                        <div className="bg-surface-active/50 rounded-lg p-2.5 text-center">
                            <div className={`text-lg font-bold ${auditData.summary.withDiscrepancy > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                {auditData.summary.withDiscrepancy}
                            </div>
                            <div className="text-[10px] text-foreground-muted uppercase tracking-wider">Discrepâncias</div>
                        </div>
                        <div className="bg-surface-active/50 rounded-lg p-2.5 text-center">
                            <div className={`text-lg font-bold ${auditData.summary.withoutAsaas > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground-strong'}`}>
                                {auditData.summary.withoutAsaas}
                            </div>
                            <div className="text-[10px] text-foreground-muted uppercase tracking-wider">Sem Asaas</div>
                        </div>
                        <div className="bg-surface-active/50 rounded-lg p-2.5 text-center">
                            <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{auditData.summary.clients}</div>
                            <div className="text-[10px] text-foreground-muted uppercase tracking-wider">Clientes</div>
                        </div>
                        <div className="bg-surface-active/50 rounded-lg p-2.5 text-center">
                            <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{auditData.summary.trials}</div>
                            <div className="text-[10px] text-foreground-muted uppercase tracking-wider">Trials</div>
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="flex items-center gap-2 px-4 pb-3">
                        <div className="relative flex-1 max-w-xs">
                            <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-foreground-muted" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Buscar empresa..."
                                className="w-full pl-8 pr-3 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 transition-all"
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-2 top-2">
                                    <X className="w-3.5 h-3.5 text-foreground-muted hover:text-foreground-strong" />
                                </button>
                            )}
                        </div>
                        <div className="flex gap-1">
                            {[
                                { key: 'all', label: 'Todos' },
                                { key: 'discrepancy', label: 'Discrepâncias' },
                                { key: 'no_asaas', label: 'Sem Asaas' },
                            ].map(({ key, label }) => (
                                <button
                                    key={key}
                                    onClick={() => setFilter(key)}
                                    className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-colors ${
                                        filter === key
                                            ? 'bg-cyan-600/20 text-cyan-700 dark:text-cyan-400 border border-cyan-500/30'
                                            : 'bg-surface-active/50 text-foreground-muted hover:text-foreground-strong border border-transparent'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={syncAll}
                            disabled={isSyncingAll}
                            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white transition-colors disabled:opacity-50"
                        >
                            {isSyncingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            Sync Todos
                        </button>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-surface-active/80 backdrop-blur-sm">
                                <tr className="text-left text-foreground-muted uppercase tracking-wider">
                                    <th className="px-4 py-2 font-medium">Empresa</th>
                                    <th className="px-3 py-2 font-medium">Plano</th>
                                    <th className="px-3 py-2 font-medium text-right">Esperado</th>
                                    <th className="px-3 py-2 font-medium text-right">Asaas</th>
                                    <th className="px-3 py-2 font-medium text-center">Desvio</th>
                                    <th className="px-3 py-2 font-medium text-right">DB MRR</th>
                                    <th className="px-3 py-2 font-medium text-center">Tipo</th>
                                    <th className="px-3 py-2 font-medium text-center">Addons</th>
                                    <th className="px-3 py-2 font-medium text-center">Usuários</th>
                                    <th className="px-3 py-2 font-medium text-center">Ação</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50">
                                {filteredAccounts.map((a) => (
                                    <tr
                                        key={a.id}
                                        className={`hover:bg-surface-hover/50 transition-colors ${
                                            a.hasDiscrepancy ? 'bg-red-500/[0.03]' : ''
                                        }`}
                                    >
                                        <td className="px-4 py-2.5">
                                            <div className="font-medium text-foreground-strong truncate max-w-[200px]" title={a.companyName}>
                                                {a.companyName}
                                            </div>
                                            <div className="text-foreground-muted font-mono text-[10px]">{a.cnpj}</div>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-cyan-500/15 text-cyan-700 dark:text-cyan-400">
                                                {a.planName}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono text-foreground-secondary">
                                            R$ {a.expectedMrr.toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono">
                                            {a.asaasFound ? (
                                                <span className="text-emerald-700 dark:text-emerald-400">
                                                    R$ {(a.asaasValue ?? 0).toFixed(2)}
                                                </span>
                                            ) : (
                                                <span className="text-foreground-muted">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-center">
                                            <DeviationBadge value={a.asaasDeviation} label="Asaas vs Esperado" />
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono text-foreground-muted">
                                            {a.dbMrrValue != null ? `R$ ${a.dbMrrValue.toFixed(2)}` : '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-center">
                                            {a.isClient ? (
                                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
                                                    <CheckCircle2 className="w-2.5 h-2.5" />
                                                    Cliente
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border border-yellow-500/20">
                                                    Trial
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-center text-foreground-muted">
                                            {a.addonsCount > 0 ? (
                                                <span title={`R$ ${a.addonsTotal.toFixed(2)}/mês`}>{a.addonsCount}</span>
                                            ) : '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-center text-foreground-muted">
                                            {a.userCount}
                                        </td>
                                        <td className="px-3 py-2.5 text-center">
                                            <button
                                                onClick={() => syncAccount(a.id, a.companyName)}
                                                disabled={syncingId === a.id}
                                                className="p-1 hover:bg-surface-active rounded transition-colors text-foreground-muted hover:text-cyan-600 dark:hover:text-cyan-400 disabled:opacity-50"
                                                title="Recalcular MRR interno"
                                            >
                                                {syncingId === a.id ? (
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-3.5 h-3.5" />
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredAccounts.length === 0 && (
                            <div className="text-center py-8 text-foreground-muted text-sm">
                                {searchTerm ? 'Nenhuma empresa encontrada' : 'Nenhuma conta com o filtro selecionado'}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
